import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ModelGrant {
  id: string;
  model_id: string;
  grantee_type: 'team' | 'user';
  team_id: string | null;
  user_id: string | null;
  created_at: string;
}

export const useModelGrants = (modelId?: string) => {
  return useQuery({
    queryKey: ['model-grants', modelId],
    enabled: !!modelId,
    queryFn: async (): Promise<ModelGrant[]> => {
      const { data, error } = await (supabase as any)
        .from('llm_model_grants')
        .select('*')
        .eq('model_id', modelId!);
      if (error) throw error;
      return (data ?? []) as ModelGrant[];
    },
  });
};

export const useAddModelGrant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { modelId: string; granteeType: 'team' | 'user'; teamId?: string; userId?: string }) => {
      const { data: auth } = await supabase.auth.getUser();
      const payload: any = {
        model_id: input.modelId,
        grantee_type: input.granteeType,
        team_id: input.granteeType === 'team' ? input.teamId : null,
        user_id: input.granteeType === 'user' ? input.userId : null,
        granted_by: auth.user?.id ?? null,
      };
      const { data, error } = await (supabase as any).from('llm_model_grants').insert(payload).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['model-grants', vars.modelId] });
      qc.invalidateQueries({ queryKey: ['admin-llm-models'] });
      qc.invalidateQueries({ queryKey: ['llm_models'] });
    },
  });
};

export const useRemoveModelGrant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; modelId: string }) => {
      const { error } = await (supabase as any).from('llm_model_grants').delete().eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['model-grants', vars.modelId] });
      qc.invalidateQueries({ queryKey: ['admin-llm-models'] });
      qc.invalidateQueries({ queryKey: ['llm_models'] });
    },
  });
};

export interface AdminUserOption {
  id: string;
  email: string;
  nome: string | null;
  avatar_url: string | null;
}

export const useAllProfiles = () => {
  return useQuery({
    queryKey: ['all-profiles'],
    queryFn: async (): Promise<AdminUserOption[]> => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, nome, avatar_url')
        .order('nome', { ascending: true });
      if (error) throw error;
      return (data ?? []) as AdminUserOption[];
    },
    staleTime: 60_000,
  });
};
